import {
  Building2,
  Compass,
  DollarSign,
  Flag,
  Globe,
  MapPin,
  Sparkles,
  Target,
  TrendingUp,
} from 'lucide-react';
import { NORTHWIND_ORG } from '@/modules/brain/data/seedCompany';
import { companyApi } from '@/modules/brain/services/company.api';
import { useCompanyResource } from '@/modules/brain/hooks/useCompanyResource';

const STAGE_LABEL: Record<string, string> = {
  'pre-seed': 'Pre-seed',
  seed: 'Seed',
  'series-a': 'Series A',
  'series-b': 'Series B',
  'series-c': 'Series C',
  growth: 'Growth',
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function OrgInfoPage() {
  const { value: org } = useCompanyResource(
    () => companyApi.getProfile(),
    NORTHWIND_ORG,
    'profile',
  );

  return (
    <div className="brain-company-page">
      <header className="brain-company-hero">
        <div className="brain-company-hero-mark">
          <span>{org.name.slice(0, 2).toUpperCase()}</span>
        </div>
        <div className="brain-company-hero-text">
          <p className="brain-company-eyebrow">Brain · Company</p>
          <h1 className="brain-company-title">{org.name}</h1>
          <p className="brain-company-tagline">{org.tagline}</p>
          <div className="brain-company-meta">
            <span><Building2 size={12} strokeWidth={1.7} /> {STAGE_LABEL[org.stage]}</span>
            <span><MapPin size={12} strokeWidth={1.7} /> {org.hqCity}, {org.hqCountry}</span>
            <span><Globe size={12} strokeWidth={1.7} /> {org.domain}</span>
            <span><TrendingUp size={12} strokeWidth={1.7} /> {org.arrBand}</span>
          </div>
        </div>
      </header>

      <section className="brain-company-section">
        <header className="brain-company-section-header">
          <Flag size={14} strokeWidth={1.6} />
          <h2>Mission</h2>
        </header>
        <p className="brain-company-prose">{org.mission}</p>
      </section>

      <div className="brain-company-grid">
        <section className="brain-company-card">
          <header className="brain-company-card-header">
            <Sparkles size={14} strokeWidth={1.6} />
            <h3>Funding</h3>
          </header>
          <dl className="brain-company-dl">
            <div>
              <dt>Last round</dt>
              <dd className="brain-company-dd-strong">
                {org.lastFundingRound.amount} · {org.lastFundingRound.round}
              </dd>
            </div>
            <div>
              <dt>Closed</dt>
              <dd>{formatDate(org.lastFundingRound.closedAt)}</dd>
            </div>
            <div>
              <dt>Lead</dt>
              <dd>{org.lastFundingRound.leadInvestor}</dd>
            </div>
            <div>
              <dt>Co-investors</dt>
              <dd>{org.lastFundingRound.coInvestors.join(', ')}</dd>
            </div>
          </dl>
        </section>

        <section className="brain-company-card">
          <header className="brain-company-card-header">
            <Building2 size={14} strokeWidth={1.6} />
            <h3>Team & location</h3>
          </header>
          <dl className="brain-company-dl">
            <div>
              <dt>Team size</dt>
              <dd className="brain-company-dd-strong">{org.teamSize} people</dd>
            </div>
            <div>
              <dt>Founded</dt>
              <dd>{org.foundedYear}</dd>
            </div>
            <div>
              <dt>HQ</dt>
              <dd>{org.hqCity}, {org.hqCountry}</dd>
            </div>
            <div>
              <dt>Remote policy</dt>
              <dd>{org.remotePolicy}</dd>
            </div>
            <div>
              <dt>Industry</dt>
              <dd>{org.industry}</dd>
            </div>
          </dl>
        </section>
      </div>

      <section className="brain-company-section">
        <header className="brain-company-section-header">
          <Target size={14} strokeWidth={1.6} />
          <h2>Customer ICP</h2>
        </header>
        <div className="brain-company-icp-grid">
          <div className="brain-company-icp-cell">
            <span className="brain-company-icp-label">Who we serve</span>
            <p>{org.icp.whoWeServe}</p>
          </div>
          <div className="brain-company-icp-cell">
            <span className="brain-company-icp-label">Wedge product</span>
            <p>{org.icp.wedgeProduct}</p>
          </div>
          <div className="brain-company-icp-cell brain-company-icp-cell-wide">
            <span className="brain-company-icp-label">Why we win</span>
            <p>{org.icp.differentiation}</p>
          </div>
        </div>
      </section>

      <section className="brain-company-section">
        <header className="brain-company-section-header">
          <Compass size={14} strokeWidth={1.6} />
          <h2>Operating values</h2>
          <span className="brain-company-section-hint">
            Cited verbatim when Lantern writes outreach copy or interview prompts.
          </span>
        </header>
        <ul className="brain-company-values">
          {org.valuesShortlist.map((v) => (
            <li key={v.title}>
              <h4>{v.title}</h4>
              <p>{v.body}</p>
            </li>
          ))}
        </ul>
      </section>

      <footer className="brain-company-footnote">
        <DollarSign size={12} strokeWidth={1.7} />
        <span>
          This page is the source of record for org context cited in candidate outreach,
          eval rationale, and pipeline rationale. Edit here, propagated everywhere.
        </span>
      </footer>
    </div>
  );
}
